<template>
  <div>
    <!-- Modalità automatica -->
    <template v-if="mode === 'auto'">
      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','First photo price')"
             :help-text="$pgettext('priceList.pack.help', 'Set the selling price of the first photo')">
        <PCurrencyInput v-model="configuration.first_photo_price" :currency="currency"/>
      </PFRow>

      <PFRow size="7x5" :separator="form_in_line"
             :label-class="form_in_line ? 'big':''"
             :label="$pgettext('priceList.pack.label','Complete package')"
             :help-text="$pgettext('priceList.pack.help', 'Choose how many photos make a complete package and its price')"
      >
        <CRow>
          <CCol sm="3">
            <label class="">{{ $pgettext('priceList.pack.label', 'N° photos') }}</label>
            <PSelectKeyValue placeholder="" :options="setNums" :value="configuration.complete_set_num" @input="onSelectNum"/>
          </CCol>
          <CCol sm="9">
            <label class="">{{ $pgettext('priceList.pack.label', 'Package price') }}</label>
            <PCurrencyInput v-model="configuration.complete_set_price" :currency="currency"/>
          </CCol>
        </CRow>
      </PFRow>

      <div class="d-flex justify-content-end">
        <CButton color="primary" @click="onApply" :disabled="!canApply">
          <translate translate-context="event_wizard">Save</translate>
        </CButton>
      </div>
    </template>

    <!-- Modalità manuale -->
    <template v-if="mode === 'manual'">
      <CRow>
        <CCol lg="6">
          <h6 class="font-weight-semi-bold mt-3">
            {{ $pgettext('priceList.pack.manual', 'Complete price list') }}
          </h6>
          <p class="text-help-dark small">
            {{
              $pgettext('priceList.pack.manual', 'The price list has been applied based on your choices. You can modify the prices generated by our algorithm.')
            }}
          </p>
        </CCol>
        <CCol sm="12" :lg="{size: '4', offset:'2' }">
          <CDataTable
            class="table-price-list"
            :items="items"
            :fields="manualFields"
            :hover="false"
            :striped="false"
            :bordered="true"
            :fixed="false"
            add-table-classes="mb-0"
          >
            <template #qty="{ item }">
              <td>
                <CInput disabled :value="item.qty" />
              </td>
            </template>
            <template #price="{ item, index }">
              <td>
                <PCurrencyInput
                  v-if="isEditableRow(index)"
                  v-model="item.price"
                  :currency="currency"
                  :is-valid="isInvalidRow(index) ? false : null"
                  @input="onManualUpdate"
                />
                <PCurrencyInput
                  v-else
                  :value="item.price"
                  :currency="currency"
                  readonly
                  disabled
                />
              </td>
            </template>
          </CDataTable>

          <PAlert v-if="!isPricesValid" color="danger" class="mt-1">
            {{
              $pgettext('priceList.pack.validation', 'Prices must be increasing: each row must cost more than the previous one.')
            }}
          </PAlert>
        </CCol>
      </CRow>


      <div class="d-flex justify-content-end mt-3">
        <CButton color="dark" variant="outline" @click="onResetToAuto" class="d-flex align-items-center">
          <CIcon name="cipRefreshCcw01" size="custom-size" :height="14" class="mr-2"/>
          <translate translate-context="priceList.pack.action">Reset price list</translate>
        </CButton>
      </div>
    </template>
  </div>
</template>

<script>

import PFRow from "@/domain/core/components/PFRow.vue";
import priceListRanges from "@/domain/core/constant/priceListRanges";
import PCurrencyInput from "@/domain/core/components/PCurrencyInput.vue";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import {mapGetters} from "vuex";
import PAlert from "@/components/PAlert.vue";

export default {
  name: "PackPriceList",
  components: {PAlert, PSelectKeyValue, PCurrencyInput, PFRow},
  emits: ['update'],
  props: {
    currency: {
      type: String,
      required: true
    },
    form_in_line: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      mode: 'auto',
      items: [],
      configuration: {
        first_photo_price: null,
        complete_set_num: null,
        complete_set_price: null,
      }
    }
  },
  computed: {
    ...mapGetters('event', ['eventCurrency']),
    setNums() {
      const result = [];
      for (let i = 2; i <= 10; i++) {
        result.push({id: i, name: String(i)});
      }
      return result;
    },
    canApply() {
      const num = this.configuration.complete_set_num
      const max = parseFloat(this.configuration.complete_set_price)
      const min = parseFloat(this.configuration.first_photo_price)
      return num !== null && num >= 2 && max > 0 && min > 0 && min < max
    },
    isPricesValid() {
      if (this.items.length < 2) return true
      for (let i = 1; i < this.items.length; i++) {
        if (parseFloat(this.items[i].price) <= parseFloat(this.items[i - 1].price))
          return false
      }
      return true
    },
    manualFields() {
      return [
        {key: 'qty', label: this.$pgettext('priceList.pack.table', 'N° photos'), _style: 'width:90px;' },
        {key: 'price', label: this.$pgettext('priceList.pack.table', 'Price')},
      ]
    }
  },
  methods: {
    onSelectNum(option) {
      this.configuration.complete_set_num = option ? option.value : null
    },
    init({items, configuration}) {
      this.configuration = {
        first_photo_price: configuration.first_photo_price ?? null,
        complete_set_price: configuration.complete_set_price ?? null,
        complete_set_num: configuration.complete_set_num ?? null
      }
      this.items = items

      if (items.length > 0 && this.configuration.first_photo_price !== null) {
        this.mode = 'manual'
      } else if (items.length > 0 && this.configuration.complete_set_price !== null) {
        // Backward compatibility: old format without first_photo_price
        this.configuration.first_photo_price = items[0].price
        this.mode = 'manual'
      } else {
        this.mode = 'auto'
      }
    },
    isEditableRow(index) {
      return index > 0 && index < this.items.length - 1
    },
    isInvalidRow(index) {
      if (this.items.length < 2) return false
      const price = parseFloat(this.items[index].price)
      if (index > 0 && price <= parseFloat(this.items[index - 1].price)) return true
      if (index < this.items.length - 1 && parseFloat(this.items[index + 1].price) <= price) return true
      return false
    },
    isValid() {
      if (this.mode === 'auto') return this.canApply
      return this.items.length > 0 && this.isPricesValid
    },
    getPriceList() {
      return {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      }
    },
    generateRows() {
      const num = parseInt(this.configuration.complete_set_num)
      const min = parseFloat(this.configuration.first_photo_price)
      const max = parseFloat(this.configuration.complete_set_price)

      if (!num || !min || !max || num < 1) return false

      if (num === 1) {
        this.items = [{qty: 1, price: max}]
        return true
      }

      const rangeMultipliers = priceListRanges.find(r => r.length === num)
      if (!rangeMultipliers) return false

      const firstMult = rangeMultipliers[0]
      const lastMult = rangeMultipliers[rangeMultipliers.length - 1]

      this.items = rangeMultipliers.map((mult, index) => {
        let price
        if (index === 0) {
          price = min
        } else if (index === num - 1) {
          price = max
        } else {
          const perc = (mult - firstMult) / (lastMult - firstMult)
          price = min + (max - min) * perc
          price = this.roundPrice(price, max)
        }
        return {qty: index + 1, price}
      })
      return true
    },
    roundPrice(value, maxAmount) {
      const rounding = Math.pow(10, Math.floor(Math.log10(maxAmount / 100)))
      let roundedValue = Math.round(value / rounding)
      return parseFloat((roundedValue * rounding).toFixed(2))
    },
    onApply() {
      if (!this.canApply) return
      if (!this.generateRows()) return

      this.mode = 'manual'
      this.emitUpdate()
    },
    onManualUpdate() {
      this.emitUpdate()
    },
    onResetToAuto() {
      if (this.items.length > 0) {
        this.configuration.first_photo_price = this.items[0].price
        this.configuration.complete_set_price = this.items[this.items.length - 1].price
        this.configuration.complete_set_num = this.items.length
      }
      this.mode = 'auto'
      this.emitUpdate()
    },
    emitUpdate() {
      this.$emit('update', {
        items: this.items,
        configuration: this.configuration,
        settings: {
          allow_upsell: true,
          allow_preorder: false,
          cta_enabled: true,
          cta_min_qty: 0,
          cta_position: 'b'
        }
      })
    }
  }
}
</script>
